import React, { memo, useMemo, useRef, useEffect } from 'react';
import Input from '../Input';
import { Icon, Popover, Checkbox } from '@firesoon/antd';
import { useSize, useSetState, useDeepCompareEffect } from 'ahooks';
import { FixedSizeList as List, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { remove, cloneDeep } from 'lodash';
import classNames from 'classnames';
import './index.less';
import { MultipleSelectProps } from './interface';

const Index = ({
  label = '',
  hideLabel = false,
  value = [],
  data = [],
  name = 'name',
  code = 'code',
  showSearch = true,
  showCheckAll = false,
  width,
  dropProps,
  onSelect,
  className = '',
  placeholder = '全部',
  dataLogModule,
  separator = '，',
  nameRender,
  dropDownListClassName,
  clearSearchWhenClose = true,
  onScroll,
}: MultipleSelectProps) => {
  const inputRef = useRef<any>();
  const size = useSize(inputRef);
  const [{ selectKeys, search, visible, list, cacheList, change, hasAllChecked, indeterminate }, setState] = useSetState<any>({
    selectKeys: cloneDeep(value),
    search: '',
    visible: false,
    list: data,
    cacheList: data, // 缓存数组
    /** 是否有触发点击 */
    change: false,
    hasAllChecked: false,
    indeterminate: false,
  });

  useDeepCompareEffect(() => {
    const unionList = (arr1, arr2) => {
      const map = new Map();
      arr1.forEach((item) => map.set(item[code], item));
      arr2.forEach((item) => map.set(item[code], item));
      return Array.from(map.values());
    };
    setState({
      list: data,
      cacheList: unionList(cacheList, data),
    });
  }, [data, code]);

  useDeepCompareEffect(() => {
    setState({
      selectKeys: cloneDeep(value),
    });
  }, [value]);

  useEffect(() => {
    const checkedAll = selectKeys.length === data.length && selectKeys.length > 0;
    const indeterminate = !checkedAll && selectKeys.length > 0;

    setState({
      hasAllChecked: checkedAll,
      indeterminate: indeterminate,
    });
  }, [selectKeys]);

  const handleSelectKeys = (e, code) => {
    if (selectKeys.includes(code)) {
      remove(selectKeys, (item) => {
        return item === code;
      });
    } else {
      selectKeys.push(code);
    }

    setState({
      selectKeys: [...selectKeys],
      change: true,
    });
  };
  const clearSelect = (e) => {
    setState({
      selectKeys: [],
    });
    onSelect?.([]);
  };
  const handleSelect = (v) => {
    if (!v && change) {
      onSelect?.(selectKeys);
    } else {
      const placedTop: any = [];
      const notSelectedList = list.filter((item) => {
        if (selectKeys.includes(item[code])) {
          placedTop.push(item);
          return false;
        } else {
          return !selectKeys.includes(item[code]);
        }
      });

      setState({
        list: [...placedTop, ...notSelectedList],
      });
    }

    // 关闭下拉框删除数据
    if (!v && clearSearchWhenClose) {
      setTimeout(() => {
        setState({
          search: '',
        });
      }, 0);
    }

    setState({
      visible: v,
      change: false,
    });
  };
  const handleSearch = (e) => {
    const value = e.target.value.trim();

    setState({
      search: value,
    });
  };

  const handleAllChecked = (e) => {
    setState({
      hasAllChecked: e.target.checked,
      selectKeys: e.target.checked ? data.map((item) => item[code]) : [],
      change: true,
    });
  };

  const selectNames = useMemo(() => {
    return cacheList.filter((item) => selectKeys.includes(item[code])).map((item) => item[name]);
  }, [selectKeys, list, cacheList]);

  const showList = useMemo(() => {
    return list.filter((item) => {
      return (
        item[name]?.toUpperCase()?.indexOf(search?.toUpperCase()) > -1 ||
        item['initials']?.toUpperCase()?.indexOf(search?.toUpperCase()) > -1
      );
    });
  }, [list, search]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const Row: any = memo(({ index, style }: any) => {
    // Data passed to List as "itemData" is available as props.data
    const item: any = showList?.[index] ?? {};

    return (
      <div
        style={style}
        title={String(item[name] ?? '')}
        className={classNames(
          'fs-multiple-select-item',
          selectKeys.includes(item[code]) && 'fs-multiple-select-checked',
        )}
      >
        <Checkbox
          onChange={(e) => handleSelectKeys(e, item[code])}
          data-log-value={label}
          checked={selectKeys.includes(item[code])}
        >
          {nameRender ? nameRender(item) : item[name]}
        </Checkbox>
      </div>
    );
  }, areEqual);

  /** 动态类名 */
  const extraClassName = useMemo(() => {
    const names = ['defalut', showSearch ? 'input' : '', showCheckAll ? 'check' : ''].filter((item) => item);
    return `fs-multiple-select-opts-search-${names.join('-')}`;
  }, [showSearch, showCheckAll]);

  const options = (
    <div className="fs-multiple-select-opts-content" data-log-module={dataLogModule}>
      <div className={`fs-multiple-select-opts-search ${extraClassName}`}>
        {showSearch && (
          <Input.Search
            placeholder={dropProps?.placeholder ?? '请输入名称'}
            value={search}
            onChange={handleSearch}
            allowClear
          />
        )}
        {showCheckAll && (
          <Checkbox onChange={handleAllChecked} checked={hasAllChecked} indeterminate={indeterminate}>
            <span className="check-all">全选</span>
          </Checkbox>
        )}
      </div>

      <div
        className="fs-multiple-select-opts-list"
        style={{ height: countHeight(showList.length, dropProps?.selectItemHeight) }}
      >
        {showList.length > 0 ? (
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                itemCount={showList.length}
                itemSize={dropProps?.selectItemHeight ?? 32}
                width={width}
                onScroll={onScroll}
              >
                {Row}
              </List>
            )}
          </AutoSizer>
        ) : (
          <div className="fs-multiple-select-opts-empty">暂无数据</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={classNames('fs-multiple-select-wrap', className)}>
      {!hideLabel && label && (
        <label htmlFor="" className="fs-multiple-select-wrap-label">
          {label}
        </label>
      )}

      <div className={classNames('fs-multiple-select')} title={selectNames.join(separator)}>
        <Popover
          content={options}
          trigger="click"
          overlayStyle={{ width: dropProps?.width ? dropProps?.width : size?.width ?? width }}
          overlayClassName={classNames('fs-multiple-select-opts', dropDownListClassName)}
          onVisibleChange={handleSelect}
          getPopupContainer={() => document.body}
          placement="bottomLeft"
        >
          <div ref={inputRef}>
            <Input
              allowClear
              disabled
              className="fs-multiple-select-show"
              suffix={
                <Icon
                  type="down"
                  className={classNames({
                    'fs-icon-up': visible,
                    'fs-icon-down': !visible,
                    selected: !!selectNames.length,
                  })}
                />
              }
              value={selectNames.join(separator)}
              placeholder={placeholder}
              style={{
                width,
              }}
            />
          </div>
        </Popover>

        {selectKeys.length > 0 && (
          <Icon type="close-circle" theme="filled" className="fs-multiple-select-clear" onClick={clearSelect} />
        )}
      </div>
    </div>
  );
};

export default Index;

function countHeight(length, height) {
  if (length > 0 && length < 5) {
    return length * (height ?? 32) + 2;
  }
  return (height ?? 32) * 5 + 4;
}
